import React from 'react';
import { Banknote, FileText, TrendingUp, Shield, Briefcase, Receipt } from 'lucide-react';

const services = [
  {
    id: 1,
    icon: Banknote,
    title: 'Loans',
    description: 'Empowering enterprises with hassle-free Corporate and MSME loans designed to meet your operational needs and expansion goals.',
    tier: 'gold',
  },
  {
    id: 2,
    icon: TrendingUp,
    title: 'Taxation Services',
    description: 'Expert tax planning, filing, and compliance services for businesses and individuals.',
    tier: 'gold',
  },
  {
    id: 3,
    icon: FileText,
    title: 'GST Registration & Filing',
    description: 'Complete GST registration, return filing, and compliance management services.',
    tier: 'silver',
  },
  {
    id: 4,
    icon: Shield,
    title: 'Internal Audit',
    description: 'Thorough internal audit to identify risks and improve operational efficiency.',
    tier: 'silver',
  },
  {
    id: 5,
    icon: Briefcase,
    title: 'Business Consulting',
    description: 'Strategic business advisory and consulting services for sustainable growth.',
    tier: 'silver',
  },
  {
    id: 6,
    icon: Receipt,
    title: 'Accounting Services',
    description: 'Professional bookkeeping, accounting, and financial reporting services.',
    tier: 'silver',
  },
];

export const FinancialServices = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="financial"
      className="py-24 md:py-32 px-6 md:px-12 bg-navy-light relative"
      data-testid="financial-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 rounded-full mb-6">
            <Briefcase size={20} className="text-gold" />
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">Financial Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Professional <span className="text-gold-gradient">Financial Solutions</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive financial and audit services tailored to your business needs.
          </p>
          <div className="section-divider mt-8" />
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className="service-card hover-lift opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`service-card-${service.id}`}
              >
                {/* Icon */}
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-lg mb-6 ${
                    service.tier === 'gold'
                      ? 'bg-gradient-to-br from-gold-metallic to-gold-dark'
                      : 'bg-gradient-to-br from-silver-dark to-silver-light'
                  }`}
                >
                  <Icon size={32} className="text-navy" />
                </div>

                {/* Tier Badge */}
                {service.tier === 'gold' && (
                  <div className="inline-block px-3 py-1 bg-gold/20 border border-gold/30 rounded-full mb-4">
                    <span className="text-gold text-xs uppercase tracking-wider font-semibold">Premium</span>
                  </div>
                )}

                {/* Content */}
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6">{service.description}</p>

                {/* CTA */}
                <button
                  onClick={scrollToContact}
                  className="text-gold font-semibold text-sm uppercase tracking-wider hover:text-gold-light transition-colors flex items-center gap-2 group"
                  data-testid={`service-inquire-btn-${service.id}`}
                >
                  Get Started
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Professional Standards */}
        <div className="mt-16 glass-panel p-8 rounded-lg text-center">
          <p className="text-silver-gradient text-lg font-semibold mb-4">
            Certified Chartered Accountants | Registered with ICAI
          </p>
          <p className="text-gray-400 mb-6">
            Our financial services are delivered by qualified professionals adhering to the highest standards of excellence.
          </p>
          
          {/* Download Brochure Button */}
          <a
            href="/agatsya-brochure.pdf"
            download
            className="inline-flex items-center gap-3 btn-gold"
            data-testid="financial-brochure-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download Services Brochure
          </a>
        </div>
      </div>
    </section>
  );
};
